/**
 * PunchTimeline
 *
 * Wave 5.9 Build 9 (2026-02) — Shared read-only lifecycle timeline for
 * a punch list item. Renders on both:
 *   - Project Hub → item detail (designer view)
 *   - Contractor Portal → item card
 *
 * Reads three sources from the item document:
 *   1. Canonical lifecycle timestamps (created_at, sent_at, first_viewed_at,
 *      acknowledged_at, completed_at, verified_at)
 *   2. `history[]` — free-form audit entries (comments, undo events, verify notes, etc.)
 *   3. Legacy fallbacks (completion_date, verified_date, contractor_acknowledged_at)
 *
 * The rendering is dark-mode aware via the `darkMode` prop. If not provided,
 * the component observes `document.documentElement.classList` for the `dark`
 * class (matches the global toggle in App.js).
 */
import { useMemo, useEffect, useState } from 'react';
import {
  Clock, Send, Eye, CheckCircle2, CheckSquare, MessageSquare,
  RotateCcw, User, Wrench, ImagePlus, RefreshCw
} from 'lucide-react';

const ACTION_META = {
  created:        { label: 'Created',                icon: Wrench,        color: 'text-slate-500' },
  sent:           { label: 'Sent to contractor',     icon: Send,          color: 'text-blue-600 dark:text-blue-400' },
  first_view:     { label: 'Contractor opened',      icon: Eye,           color: 'text-cyan-600 dark:text-cyan-400' },
  acknowledged:   { label: 'Acknowledged',           icon: CheckCircle2,  color: 'text-teal-600 dark:text-teal-400' },
  complete:       { label: 'Marked complete',        icon: CheckCircle2,  color: 'text-green-600 dark:text-green-400' },
  undo_complete:  { label: 'Completion undone',      icon: RotateCcw,     color: 'text-amber-600 dark:text-amber-400' },
  verify:         { label: 'Verified',               icon: CheckSquare,   color: 'text-purple-600 dark:text-purple-400' },
  undo_verify:    { label: 'Verification undone',    icon: RotateCcw,     color: 'text-amber-600 dark:text-amber-400' },
  reopen:         { label: 'Reopened',               icon: RefreshCw,     color: 'text-orange-600 dark:text-orange-400' },
  resend:         { label: 'Re-sent',                icon: Send,          color: 'text-blue-600 dark:text-blue-400' },
  reassign:       { label: 'Reassigned',             icon: User,          color: 'text-indigo-600 dark:text-indigo-400' },
  comment:        { label: 'Comment',                icon: MessageSquare, color: 'text-slate-500 dark:text-slate-300' },
  after_photo:    { label: 'After photo uploaded',   icon: ImagePlus,     color: 'text-sky-600 dark:text-sky-400' },
};

const fmtDateTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return null; }
};

const useObservedDarkMode = (explicit) => {
  const [dark, setDark] = useState(() => {
    if (typeof explicit === 'boolean') return explicit;
    if (typeof document === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });
  useEffect(() => {
    if (typeof explicit === 'boolean') { setDark(explicit); return; }
    if (typeof document === 'undefined') return;
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [explicit]);
  return dark;
};

export default function PunchTimeline({ item, darkMode, className = '' }) {
  const isDark = useObservedDarkMode(darkMode);

  const events = useMemo(() => {
    if (!item) return [];
    const seen = new Set();
    const evts = [];

    const push = (action, at, extras = {}) => {
      if (!at) return;
      const key = `${action}:${at}`;
      if (seen.has(key)) return;
      seen.add(key);
      evts.push({ action, at, ...extras });
    };

    // Canonical timestamps (with legacy fallbacks).
    push('created', item.created_at || item.created_date);
    push('sent', item.sent_at || item.assigned_at);
    push('first_view', item.first_viewed_at);
    push('acknowledged', item.acknowledged_at || item.contractor_acknowledged_at);
    push('complete', item.completed_at || item.completion_date);
    push('verify', item.verified_at || item.verified_date);

    // history[] entries — richer detail (comment, actor).
    (item.history || []).forEach((h) => {
      if (!h) return;
      const action = h.action || 'comment';
      const at = h.at || h.timestamp;
      if (!at) return;
      const key = `${action}:${at}`;
      if (seen.has(key)) {
        // Merge extras (actor_name, comment) into the existing entry.
        const existing = evts.find((e) => e.action === action && e.at === at);
        if (existing) {
          if (!existing.actor_name) existing.actor_name = h.actor_name;
          if (!existing.comment) existing.comment = h.comment;
        }
        return;
      }
      seen.add(key);
      evts.push({
        action,
        at,
        actor_name: h.actor_name,
        actor_type: h.actor_type,
        comment: h.comment,
        from_status: h.from_status,
        to_status: h.to_status,
      });
    });

    // Sort ascending by timestamp — oldest first.
    evts.sort((a, b) => new Date(a.at) - new Date(b.at));
    return evts;
  }, [item]);

  if (!events.length) {
    return (
      <div
        data-testid="punch-timeline-empty"
        className={`text-xs italic ${isDark ? 'text-slate-500' : 'text-slate-400'} ${className}`}
      >
        No lifecycle activity yet.
      </div>
    );
  }

  return (
    <div
      data-testid="punch-timeline"
      className={`relative pl-5 space-y-3 ${className}`}
      role="log"
      aria-label="Punch item timeline"
    >
      {/* Vertical rail */}
      <div
        aria-hidden="true"
        className={`absolute left-[9px] top-1 bottom-1 w-px ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}
      />
      {events.map((e, idx) => {
        const meta = ACTION_META[e.action] || ACTION_META.comment;
        const Icon = meta.icon || Clock;
        return (
          <div
            key={`${e.action}-${e.at}-${idx}`}
            className="relative flex items-start gap-2"
            data-testid={`punch-timeline-event-${e.action}`}
          >
            <div
              className={`absolute -left-5 mt-0.5 flex items-center justify-center w-[18px] h-[18px] rounded-full ${
                isDark ? 'bg-[#0D0D0D] ring-1 ring-slate-700' : 'bg-white ring-1 ring-slate-200'
              }`}
            >
              <Icon className={`w-3 h-3 ${meta.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-xs font-medium ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
                {meta.label}
                {e.actor_name ? (
                  <span className={`ml-1 font-normal ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    · {e.actor_name}
                  </span>
                ) : null}
              </div>
              <div className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {fmtDateTime(e.at)}
              </div>
              {e.comment ? (
                <div className={`mt-1 text-xs whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  “{e.comment}”
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
